import { API_BASE, API_BASE_DEFAULT } from './nav';

/**
 * ~60-line regex tokenizer over the RiiKit CODE colors — the kit's
 * convention (no highlighter dependency). Languages: json, bash, http,
 * plus light js/py modes for the fetch/requests panes and txt for transcripts.
 */

export type CodeLang = 'bash' | 'json' | 'http' | 'js' | 'py' | 'txt';

export interface Seg {
  c: string;
  t: string;
}

/** RiiKit CODE map verbatim (per the design handoff). */
export const COL = {
  com: '#7a7a88',
  key: '#ff7eae',
  str: '#84d68c',
  num: '#7fb0ff',
  bool: '#f0c451',
  pun: '#9a9aae',
  txt: '#e7e7f0',
} as const;

/** Method chip colors — color-mixed toward ink for contrast at render time. */
export const METHOD_COLOR: Record<string, string> = {
  GET: 'var(--color-data-green)',
  POST: 'var(--color-data-blue)',
  PATCH: 'var(--color-data-amber)',
  DELETE: 'var(--color-brand-600)',
};

export const methodChipStyle = (method?: string) => {
  const c = (method && METHOD_COLOR[method]) || 'var(--color-data-cyan)';
  return {
    color: `color-mix(in srgb, ${c} 72%, var(--color-ink))`,
    borderColor: `color-mix(in srgb, ${c} 40%, transparent)`,
    background: `color-mix(in srgb, ${c} 10%, white)`,
  };
};

const hlJson = (line: string): Seg[] => {
  const out: Seg[] = [];
  const re = /("(?:[^"\\]|\\.)*")(\s*:)|("(?:[^"\\]|\\.)*")|(-?\d+(?:\.\d+)?)|\b(true|false|null)\b/g;
  let i = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(line))) {
    if (m.index > i) out.push({ c: COL.pun, t: line.slice(i, m.index) });
    if (m[1] != null) {
      out.push({ c: COL.key, t: m[1] });
      out.push({ c: COL.pun, t: m[2] });
    } else if (m[3] != null) out.push({ c: COL.str, t: m[3] });
    else if (m[4] != null) out.push({ c: COL.num, t: m[4] });
    else out.push({ c: COL.bool, t: m[5] });
    i = re.lastIndex;
  }
  if (i < line.length) out.push({ c: COL.pun, t: line.slice(i) });
  return out;
};

const hlBash = (line: string): Seg[] => {
  if (/^\s*#/.test(line)) return [{ c: COL.com, t: line }];
  if (/^\s*["{}\]]/.test(line)) return hlJson(line);
  const out: Seg[] = [];
  const re = /("(?:[^"\\]|\\.)*")|('(?:[^'\\]|\\.)*')|(\s--?[A-Za-z][\w-]*)|(\b(?:curl|export|npm|pnpm|yarn|bun|pip|claude)\b)|(https?:\/\/[^\s"'\\]+)/g;
  let i = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(line))) {
    if (m.index > i) out.push({ c: COL.txt, t: line.slice(i, m.index) });
    if (m[1] != null) out.push({ c: COL.str, t: m[1] });
    else if (m[2] != null) out.push({ c: COL.str, t: m[2] });
    else if (m[3] != null) out.push({ c: COL.com, t: m[3] });
    else if (m[4] != null) out.push({ c: COL.key, t: m[4] });
    else out.push({ c: COL.txt, t: m[5] });
    i = re.lastIndex;
  }
  if (i < line.length) out.push({ c: COL.txt, t: line.slice(i) });
  return out;
};

const hlHttp = (line: string): Seg[] => {
  if (/^HTTP\//.test(line)) return [{ c: COL.bool, t: line }];
  if (/^\s*["{}\]]/.test(line)) return hlJson(line);
  const m = line.match(/^([A-Za-z0-9-]+:)(.*)$/);
  if (m) {
    const segs: Seg[] = [{ c: COL.key, t: m[1] }];
    if (/^\s*\{/.test(m[2])) return segs.concat(hlJson(m[2]));
    segs.push({ c: /^\s*\d/.test(m[2]) ? COL.num : COL.txt, t: m[2] });
    return segs;
  }
  return [{ c: COL.txt, t: line }];
};

/** Shared string/comment/keyword/number pass for the js/py panes. */
const hlCode = (line: string, comment: RegExp, keywords: RegExp): Seg[] => {
  if (comment.test(line)) return [{ c: COL.com, t: line }];
  const out: Seg[] = [];
  const re = new RegExp(
    `("(?:[^"\\\\]|\\\\.)*")|('(?:[^'\\\\]|\\\\.)*')|(${keywords.source})|(-?\\b\\d+(?:\\.\\d+)?\\b)`,
    'g',
  );
  let i = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(line))) {
    if (m.index > i) out.push({ c: COL.txt, t: line.slice(i, m.index) });
    if (m[1] != null) out.push({ c: COL.str, t: m[1] });
    else if (m[2] != null) out.push({ c: COL.str, t: m[2] });
    else if (m[3] != null) out.push({ c: COL.key, t: m[3] });
    else out.push({ c: COL.num, t: m[4] });
    i = re.lastIndex;
  }
  if (i < line.length) out.push({ c: COL.txt, t: line.slice(i) });
  return out;
};

const hlJs = (line: string): Seg[] =>
  hlCode(line, /^\s*\/\//, /\b(?:import|from|const|let|await|async|new|try|catch|if|throw|return|process)\b/);

const hlPy = (line: string): Seg[] =>
  hlCode(line, /^\s*#/, /\b(?:import|from|def|return|try|except|raise|print|os)\b/);

const FN: Record<CodeLang, (line: string) => Seg[]> = {
  json: hlJson,
  bash: hlBash,
  http: hlHttp,
  js: hlJs,
  py: hlPy,
  txt: (line) => [{ c: COL.txt, t: line }],
};

/** Applies the apiBase swap, splits into lines, highlights each. */
export const codeLines = (src: string, lang: CodeLang): Seg[][] => {
  const swapped = API_BASE === API_BASE_DEFAULT ? src : src.split(API_BASE_DEFAULT).join(API_BASE);
  return swapped
    .split('\n')
    .map((l) => (l.length ? FN[lang](l) : [{ c: COL.txt, t: ' ' }]));
};

/** The copyable text of a pane (apiBase swap applied, colors dropped). */
export const copyText = (src: string): string =>
  API_BASE === API_BASE_DEFAULT ? src : src.split(API_BASE_DEFAULT).join(API_BASE);
